import { createTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { breakpoints, colors } from './muiThemeConfig'

const createMuiTheme = (darkModeEnabled: boolean) => {
	const mode = darkModeEnabled ? 'dark' : 'light'

	return createTheme({
		palette: {
			mode,
			primary: {
				main: colors.primary[mode],
			},
		},
		breakpoints: { values: breakpoints },
		components: {
			MuiInputBase: {
				styleOverrides: {
					root: {
						'&.Mui-disabled': {
							color: colors.disabled[mode],
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: colors.border[mode],
							},
						},
					},
					input: {
						color: colors.text[mode],
						'&.Mui-disabled, &[readonly]': {
							WebkitTextFillColor: colors.disabled[mode],
							cursor: 'default',
						},
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						'&.Mui-disabled, &[readonly]': {
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: colors.border[mode],
							},
						},
					},
					notchedOutline: {
						borderColor: colors.border[mode],
					},
				},
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						color: colors.text[mode],
					},
					icon: {
						color: colors.text[mode],
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						color: colors.text[mode],
						'&.Mui-focused': {
							color: colors.primary[mode],
						},
						'&.Mui-disabled': {
							color: colors.disabled[mode],
						},
					},
				},
			},
			MuiInputAdornment: {
				styleOverrides: {
					root: {
						color: colors.text[mode],
						'& .MuiTypography-root': {
							color: colors.text[mode],
						},
					},
				},
			},
			MuiSwitch: {
				styleOverrides: {
					root: {
						width: 42,
						height: 26,
						padding: 0,
					},
					switchBase: {
						padding: 1,
						'&.Mui-checked': {
							transform: 'translateX(16px)',
							color: '#fff',
							'& + .MuiSwitch-track': {
								backgroundColor: colors.primary[mode],
								opacity: 1,
							},
						},
					},
					thumb: {
						width: 24,
						height: 24,
					},
					track: {
						borderRadius: 13,
						backgroundColor: colors.switch.track[mode],
					},
				},
			},
			MuiFormLabel: {
				styleOverrides: {
					root: {
						color: colors.text[mode],
						'&.Mui-focused': {
							color: colors.primary[mode],
						},
						'&.Mui-disabled': {
							color: colors.disabled[mode],
						},
					},
				},
			},
			MuiFormHelperText: {
				styleOverrides: {
					root: {
						color: colors.text[mode],
						'&.Mui-disabled': {
							color: colors.disabled[mode],
						},
					},
				},
			},
			MuiStepper: {
				styleOverrides: {
					root: {
						'& .MuiStepConnector-line': {
							borderColor: colors.border[mode],
						},
					},
				},
			},
			MuiStepLabel: {
				styleOverrides: {
					label: {
						color: colors.text[mode],
						'&.Mui-active': {
							color: colors.primary[mode],
						},
						'&.Mui-completed': {
							color: colors.primary[mode],
						},
					},
				},
			},
			MuiStepIcon: {
				styleOverrides: {
					root: {
						color: colors.border[mode],
						'&.Mui-active': {
							color: colors.primary[mode],
							text: {
								fill: '#ffffff',
							},
						},
						'&.Mui-completed': {
							color: colors.primary[mode],
						},
					},
				},
			},
			MuiMenu: {
				styleOverrides: {
					paper: {
						backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
						border: `1px solid ${colors.border[mode]}`,
						borderRadius: '8px',
						boxShadow:
							mode === 'dark' ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
					},
				},
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						color: colors.text[mode],
						'&:hover': {
							backgroundColor:
								mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
						},
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						backgroundColor: mode === 'dark' ? '#0d2d30' : '#ffffff',
						color: colors.text[mode],
						boxShadow:
							mode === 'dark' ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
						borderRadius: '8px',
						transition: 'box-shadow 0.3s ease-in-out',
						'&:hover': {
							boxShadow:
								mode === 'dark'
									? '0 6px 10px rgba(0, 0, 0, 0.4)'
									: '0 6px 10px rgba(0, 0, 0, 0.15)',
						},
					},
				},
			},
			MuiCardHeader: {
				styleOverrides: {
					root: {
						padding: '16px',
					},
					title: {
						color: colors.text[mode],
						fontWeight: 500,
					},
					subheader: {
						color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
					},
				},
			},
			MuiCardContent: {
				styleOverrides: {
					root: {
						padding: '16px',
						'&:last-child': {
							paddingBottom: '16px',
						},
					},
				},
			},
			MuiCardActions: {
				styleOverrides: {
					root: {
						padding: '8px 16px',
					},
				},
			},
			MuiAccordion: {
				styleOverrides: {
					root: {
						backgroundColor: colors.accordion.background[mode],
						color: colors.accordion.text[mode],
						border: `1px solid ${colors.accordion.border[mode]}`,
						'&:before': {
							display: 'none',
						},
					},
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					root: {
						backgroundColor: colors.accordion.header[mode],
						color: colors.accordion.text[mode],
					},
					content: {
						margin: 0,
						'&.Mui-expanded': {
							margin: 0,
						},
					},
				},
			},
			MuiAccordionDetails: {
				styleOverrides: {
					root: {
						padding: '16px',
						backgroundColor: colors.accordion.background[mode],
					},
				},
			},
		},
	})
}

const isDarkMode = () => document.body.classList.contains('dark')

export const useMuiTheme = () => {
	const [theme, setTheme] = useState(() => createMuiTheme(isDarkMode()))

	useEffect(() => {
		const observer = new MutationObserver(() => {
			setTheme(createMuiTheme(isDarkMode()))
		})

		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		})

		return () => observer.disconnect()
	}, [])

	return theme
}

export default useMuiTheme
